import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  getLeavesByEmployeeId,
  createLeaveRequest,
  getLeaveBalanceFromUserAndMeta,
} from "../utils/methods";
import { UploadImage, InputComp } from "../components";
import { strings } from "../utils/strings";
import Button from "../components/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import useFetchOptions from "../hooks/useFetchOptions";
import { toast } from "react-toastify";

const LeaveRequestForm = () => {
  const { id: currentId } = useParams();
  const navigate = useNavigate();

  const [leavesData, setLeavesData] = useState({});
  const [leaves, setLeaves] = useState({});
  const [leaveBalance, setLeaveBalance] = useState(0);
  const [leavesType, setLeavesType] = useState("");
  const [leaveStartingDate, setLeaveStartingDate] = useState("");
  const [leaveEndingDate, setLeaveEndingDate] = useState("");
  const [leaveExplanation, setLeaveExplanation] = useState("");
  const [certificateImageUrl, setCertificateImageUrl] = useState("");
  const { leaveTypes, loading, error } = useFetchOptions();
  const [disable, setDisable] = useState("");

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchLeavesAndBalance(user.uid);
      } else {
        console.error("User is not authenticated.");
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchLeavesAndBalance = async (userId) => {
    try {
      const { leaveBalance } = await getLeaveBalanceFromUserAndMeta(userId);
      const fetchedLeaves = await getLeavesByEmployeeId(userId);
      setLeaves(fetchedLeaves);
      setLeaveBalance(leaveBalance);
    } catch (error) {
      console.error("Error fetching leaves and balance:", error);
    }
  };

  useEffect(() => {
    const loadLeaveData = async () => {
      if (currentId !== "new") {
        try {
          const leaves = await getLeavesByEmployeeId(currentId);
          if (leaves.length) {
            const leave = leaves;
            setLeavesData(leave);
            setLeaveStartingDate(leave.startingDate || "");
            setLeaveEndingDate(leave.endingDate || "");
            setLeaveExplanation(leave.explanation || "");
            setCertificateImageUrl(leave.details || "");
          }
        } catch (error) {
          console.error("Error loading leaves:", error);
        }
      }
    };
    loadLeaveData();
  }, [currentId]);

  const handleInputChange = (key, value) => {
    setLeavesData((prevData) => ({ ...prevData, [key]: value }));
    if (key === "startingDate") setLeaveStartingDate(value);
    if (key === "endingDate") setLeaveEndingDate(value);
  };

  const handleOptionChange = (key, newValue) => {
    setLeavesType(newValue || "");
    setLeavesData((prevData) => ({
      ...prevData,
      [key]: newValue || "",
    }));
  };

  const handleCertificateImageUpload = (url) => {
    setCertificateImageUrl(url);
    setLeavesData((prevData) => ({ ...prevData, details: url }));
  };
  const handleSave = async () => {
    const fieldsToValidate = [
      {
        key: "leavesType",
        value: leavesType,
        label: strings["leaveType"],
        regex: null,
      },
      {
        key: "leaveStartingDate",
        value: leaveStartingDate,
        label: strings["startDate"],
        regex: null,
      },
      {
        key: "leaveEndingDate",
        value: leaveEndingDate,
        label: strings["endDate"],
        regex: null,
      },
      {
        key: "leaveExplanation",
        value: leaveExplanation,
        label: strings["details"],
        regex: null,
      },
    ];

    let isFormValid = true;

    fieldsToValidate.forEach(({ key, value, label, regex }) => {
      if (!value || (regex && !regex.test(value))) {
        isFormValid = false;
        toast.error(`${label} is required and must be valid.`);
      }
    });

    if (!isFormValid) return;

    if (!leaveExplanation.trim()) {
      toast.error(`${strings["leaveExplanation"]} is required.`);
      return;
    }

    // Leave request creation logic
    const startDate = new Date(leaveStartingDate);
    const endDate = new Date(leaveEndingDate);
    const leaveDays =
      Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    const leaveBalanceKeys = {
      "Sick Leave": "sl",
      "Casual Leave": "cl",
      "Annual Leave": "al",
    };

    const leaveKey = leaveBalanceKeys[leavesType];
    const leaveTypeBalance = leaveBalance[leaveKey];

    if (!leaveTypeBalance || leaveTypeBalance < leaveDays) {
      toast.error(`Insufficient balance for ${leavesType}.`);
      return;
    }

    try {
      const leaveRequestData = {
        leaveType: leavesType,
        leaveStartingDate,
        leaveEndingDate,
        explanation: leaveExplanation,
        details: certificateImageUrl,
        status: "Waiting for Manager Approval",
      };

      await createLeaveRequest(leaveRequestData);
      navigate(`/leaveRequest`);
    } catch (error) {
      console.error("Error saving leave request:", error);
    }
  };

  const getRemainingLeaveBalance = () => {
    if (leavesType === "Sick Leave") {
      return leaveBalance.sl || 0;
    }
    if (leavesType === "Casual Leave") {
      return leaveBalance.cl || 0;
    }
    if (leavesType === "Annual Leave") {
      return leaveBalance.al || 0;
    }

    return leaveBalance.totalLeaves;
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error loading options: {error.message}</p>;

  return (
    <div className="bg-border w-full h-full overflow-scroll px-[212px] pt-12 pb-[60px]">
      <p className="text-[36px] font-semibold">{strings["LeaveFormTitle"]}</p>
      <div className="mt-5">
        <div className="mb-[6px]">
          <label className="text-sm font-medium text-color-1">
            {strings["leaveType"]}
          </label>
        </div>
        <Autocomplete
          className="w-full"
          options={leaveTypes}
          getOptionLabel={(option) => option || ""}
          value={leavesType}
          onChange={(event, newValue) =>
            handleOptionChange("leavesType", newValue)
          }
          disableClearable // Removes the close button
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Select"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 44,
                  padding: 0,
                  paddingLeft: 1,
                  borderRadius: "8px",
                  backgroundColor: "white",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiInputBase-input": {
                  height: "100%",
                  padding: "0 12px",
                  textAlign: "start",
                  boxSizing: "border-box",
                },
              }}
            />
          )}
        />
      </div>

      <div className="bg-color-16 border-2 border-primary mt-6 rounded-md h-[44px]">
        <div className="flex justify-between items-center px-[14px] py-[10px]">
          <p className="text-primary text-base font-normal">
            {strings["leaveAvailable"]}
          </p>
          <p className="text-primary text-base font-normal">
            {getRemainingLeaveBalance()} {/* Show updated balance */}
          </p>
        </div>
      </div>

      {/* Other form inputs remain unchanged */}
      <InputComp
        label={strings["startDate"]}
        placeHolder={strings["startDateplaceholder"]}
        type="date"
        value={leaveStartingDate}
        handleChange={(key, value) => handleInputChange("startingDate", value)}
        inputKey="startingDate"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
      />
      <InputComp
        label={strings["endDate"]}
        placeHolder={strings["endDatePlaceholder"]}
        type="date"
        value={leaveEndingDate}
        handleChange={(key, value) => handleInputChange("endingDate", value)}
        inputKey="endingDate"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
      />
      <InputComp
        label={strings["details"]}
        placeHolder={strings["detailPlaceholder"]}
        type="text"
        value={leaveExplanation}
        handleChange={(key, value) => setLeaveExplanation(value)}
        inputKey="leaveExplanation"
        labelClasses="text-sm font-medium pb-[6px] pt-5 text-color-1"
        classes="h-11 text-color-1 border-color-9 hover:border-black border-1"
      />
      <div className="mt-[20px] mb-6">
        <div className="mb-[6px]">
          <label className="text-sm font-medium text-color-1">
            {strings["addDocument"]}
          </label>
        </div>
        <UploadImage
          onImageUpload={handleCertificateImageUpload}
          blogId={currentId === "new" ? null : currentId}
          imageType="leaveCertificate"
          initialImageUrl={leaves.details}
        />
      </div>
      <div className="flex justify-center w-full">
        <Button
          classes={`!w-full justify-center ${
            disable ? "opacity-50 cursor-not-allowed" : ""
          }`}
          onClick={disable ? null : handleSave}
          disabled={disable}
        >
          {strings["submitButton"]}
        </Button>
      </div>
    </div>
  );
};

export default LeaveRequestForm;
